import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { formatTime12h } from '../utils/time';
import Calendar from './Calendar';
import toast from 'react-hot-toast';

export default function AdminView({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [meds, setMeds] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showAddMed, setShowAddMed] = useState(false);
  const [editingEmailId, setEditingEmailId] = useState(null);
  const [editingEmailValue, setEditingEmailValue] = useState('');
  const [newUser, setNewUser] = useState({ name: '', email: '', reminder_time: '08:00', timezone: 'America/Denver' });
  const [newMed, setNewMed] = useState({
    name: '', type: 'daily', default_dosage: '', dosage_unit: '',
    interval_days: '', next_due_date: '', schedule_type: 'floating', reminder_time: ''
  });
  const [overdue, setOverdue] = useState([]);
  const [dayDetail, setDayDetail] = useState(null);
  const [dayDetailLoading, setDayDetailLoading] = useState(false);

  useEffect(() => { loadUsers(); loadOverdue(); }, []);

  async function loadUsers() {
    try {
      const data = await api.getUsers();
      setUsers(data);
      if (!selectedUser && data.length > 0) setSelectedUser(data[0]);
    } catch { toast.error('Could not load users'); }
  }

  async function loadOverdue() {
    try {
      setOverdue(await api.getOverdueMedications());
    } catch { toast.error('Could not load overdue medications'); }
  }

  useEffect(() => {
    if (!selectedUser) return;
    setDayDetail(null);
    api.getMedications(selectedUser.id).then(setMeds).catch(() => toast.error('Could not load medications'));
  }, [selectedUser]);

  async function handleDayClick(dateStr) {
    if (!selectedUser) return;
    setDayDetailLoading(true);
    try {
      setDayDetail(await api.getDay(selectedUser.id, dateStr));
    } catch { toast.error('Could not load that day'); }
    finally { setDayDetailLoading(false); }
  }

  async function handleAddUser(e) {
    e.preventDefault();
    try {
      const user = await api.createUser(newUser);
      toast.success(`${user.name} invited — tell them to sign in at mednudge.org with ${user.email}`);
      setShowAddUser(false);
      setNewUser({ name: '', email: '', reminder_time: '08:00', timezone: 'America/Denver' });
      loadUsers();
    } catch (err) { toast.error(err.message); }
  }

  async function handleSetEmail(id) {
    if (!editingEmailValue.trim()) return;
    try {
      await api.updateUser(id, { email: editingEmailValue.trim() });
      toast.success('Email saved');
      setEditingEmailId(null);
      setEditingEmailValue('');
      loadUsers();
    } catch (err) { toast.error(err.message); }
  }

  async function handleAddMed(e) {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await api.createMedication({
        ...newMed,
        user_id: selectedUser.id,
        default_dosage: newMed.default_dosage || null,
        schedule_type: newMed.type === 'interval' ? newMed.schedule_type : null,
        reminder_time: newMed.type === 'daily' ? (newMed.reminder_time || null) : null,
      });
      toast.success('Medication added');
      setShowAddMed(false);
      setNewMed({ name: '', type: 'daily', default_dosage: '', dosage_unit: '', interval_days: '', next_due_date: '', schedule_type: 'floating', reminder_time: '' });
      api.getMedications(selectedUser.id).then(setMeds);
    } catch (err) { toast.error(err.message); }
  }

  async function handleDeleteMed(id) {
    if (!window.confirm('Deactivate this medication?')) return;
    try {
      await api.deleteMedication(id);
      toast.success('Medication deactivated');
      api.getMedications(selectedUser.id).then(setMeds);
    } catch (err) { toast.error(err.message); }
  }

  async function handleDeleteUser(id) {
    if (!window.confirm('Delete this user and all their data?')) return;
    try {
      await api.deleteUser(id);
      toast.success('User deleted');
      setSelectedUser(null);
      loadUsers();
    } catch (err) { toast.error(err.message); }
  }

  function formatMedType(med) {
    if (med.type === 'daily') return 'Daily';
    if (med.type === 'as_needed') return 'As Needed';
    if (med.type === 'interval') {
      const sched = med.schedule_type === 'fixed' ? 'fixed' : 'floating';
      return `Every ${med.interval_days}d (${sched})`;
    }
    return med.type;
  }

  return (
    <div className="admin-page">
      {overdue.length > 0 && (
        <div className="meds-section overdue-section">
          <div className="section-header">
            <h3>⚠️ Overdue Medications</h3>
          </div>
          <table className="meds-table">
            <thead>
              <tr>
                <th>Medication</th><th>Family Member</th><th>Schedule</th><th>Was Due</th><th>Last Taken</th>
              </tr>
            </thead>
            <tbody>
              {overdue.map(o => (
                <tr key={o.medication_id}>
                  <td>{o.name}</td>
                  <td>{o.user_name}</td>
                  <td>Every {o.interval_days}d ({o.schedule_type || 'floating'})</td>
                  <td className="overdue-cell">
                    {o.next_due_date}
                    <span className="overdue-days">{o.days_overdue} day{o.days_overdue === 1 ? '' : 's'} overdue</span>
                  </td>
                  <td>{o.last_taken_at ? new Date(o.last_taken_at).toLocaleString() : 'Never'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    <div className="admin-view">
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <h2>Family Members</h2>
          <button className="btn-small" onClick={() => setShowAddUser(!showAddUser)}>+ Add</button>
        </div>

        {showAddUser && (
          <form className="inline-form" onSubmit={handleAddUser}>
            <input placeholder="Name" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} required />
            <input type="email" placeholder="Email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} required />
            <input type="time" value={newUser.reminder_time} onChange={e => setNewUser({...newUser, reminder_time: e.target.value})} />
            <select value={newUser.timezone} onChange={e => setNewUser({...newUser, timezone: e.target.value})}>
              <option value="America/New_York">Eastern</option>
              <option value="America/Chicago">Central</option>
              <option value="America/Denver">Mountain</option>
              <option value="America/Los_Angeles">Pacific</option>
            </select>
            <button type="submit" className="btn-primary btn-small">Invite</button>
          </form>
        )}

        <ul className="user-list">
          {users.map(u => (
            <li key={u.id} className={`user-item ${selectedUser?.id === u.id ? 'active' : ''}`}>
              <span onClick={() => setSelectedUser(u)}>{u.name}</span>
              <span className={`role-badge ${u.role}`}>{u.role}</span>
              <span className={`role-badge ${u.is_active ? 'admin' : 'member'}`}>
                {u.is_active ? 'Active' : u.email ? 'Invited' : 'No email'}
              </span>
              {!u.email && editingEmailId !== u.id && (
                <button className="btn-link" onClick={(e) => { e.stopPropagation(); setEditingEmailId(u.id); setEditingEmailValue(''); }}>
                  Set email
                </button>
              )}
              {editingEmailId === u.id && (
                <form className="inline-form" onClick={e => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); handleSetEmail(u.id); }}>
                  <input type="email" placeholder="Email" value={editingEmailValue} onChange={e => setEditingEmailValue(e.target.value)} autoFocus required />
                  <button type="submit" className="btn-primary btn-small">Save</button>
                </form>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="admin-main">
        {selectedUser ? (
          <>
            <div className="admin-user-header">
              <h2>{selectedUser.name}</h2>
              <div className="admin-user-actions">
                <button className="btn-secondary btn-small" onClick={() => api.exportCSV(selectedUser.id, selectedUser.name)}>⬇ Export CSV</button>
                {selectedUser.id !== currentUser.id && (
                  <button className="btn-danger btn-small" onClick={() => handleDeleteUser(selectedUser.id)}>Delete User</button>
                )}
              </div>
            </div>

            <div className="meds-section">
              <div className="section-header">
                <h3>Medications</h3>
                <button className="btn-small" onClick={() => setShowAddMed(!showAddMed)}>+ Add Med</button>
              </div>

              {showAddMed && (
                <form className="inline-form med-form" onSubmit={handleAddMed}>
                  <input placeholder="Medication name" value={newMed.name} onChange={e => setNewMed({...newMed, name: e.target.value})} required />
                  <select value={newMed.type} onChange={e => setNewMed({...newMed, type: e.target.value})}>
                    <option value="daily">Daily</option>
                    <option value="interval">Every N Days</option>
                    <option value="as_needed">As Needed</option>
                  </select>
                  <input type="number" step="any" placeholder="Default dosage" value={newMed.default_dosage} onChange={e => setNewMed({...newMed, default_dosage: e.target.value})} />
                  <input placeholder="Unit (mg, ml…)" value={newMed.dosage_unit} onChange={e => setNewMed({...newMed, dosage_unit: e.target.value})} />
                  {newMed.type === 'daily' && (
                    <div className="reminder-time-field">
                      <label>
                        Reminder time (optional)
                        <input type="time" value={newMed.reminder_time} onChange={e => setNewMed({...newMed, reminder_time: e.target.value})} />
                      </label>
                      <small>Leave blank to use {selectedUser.name}'s default ({formatTime12h(selectedUser.reminder_time)}).</small>
                    </div>
                  )}
                  {newMed.type === 'interval' && (
                    <>
                      <input type="number" placeholder="Every N days" value={newMed.interval_days} onChange={e => setNewMed({...newMed, interval_days: e.target.value})} required />
                      <input type="date" placeholder="First due date" value={newMed.next_due_date} onChange={e => setNewMed({...newMed, next_due_date: e.target.value})} required />
                      <div className="schedule-type-field">
                        <label className="schedule-type-label">Schedule type</label>
                        <div className="schedule-type-options">
                          <label className="schedule-radio">
                            <input type="radio" name="schedule_type" value="floating"
                              checked={newMed.schedule_type === 'floating'}
                              onChange={() => setNewMed({...newMed, schedule_type: 'floating'})} />
                            <span>
                              <strong>Floating</strong>
                              <small>Next dose is N days after it was actually taken. Schedule shifts if taken late.</small>
                            </span>
                          </label>
                          <label className="schedule-radio">
                            <input type="radio" name="schedule_type" value="fixed"
                              checked={newMed.schedule_type === 'fixed'}
                              onChange={() => setNewMed({...newMed, schedule_type: 'fixed'})} />
                            <span>
                              <strong>Fixed</strong>
                              <small>Next dose is N days after the scheduled date. Schedule stays consistent even if taken late.</small>
                            </span>
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                  <button type="submit" className="btn-primary btn-small">Add</button>
                </form>
              )}

              <table className="meds-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Type</th><th>Reminder Time</th><th>Default Dose</th><th>Next Due</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {meds.map(m => (
                    <tr key={m.id}>
                      <td>{m.name}</td>
                      <td><span className={`type-badge ${m.type}`}>{formatMedType(m)}</span></td>
                      <td className="reminder-cell">
                        {m.type !== 'daily' ? '—' : m.reminder_time ? (
                          <>
                            <span className="override">{formatTime12h(m.effective_reminder_time)}</span>
                            <span className="inherited">override</span>
                          </>
                        ) : (
                          <>
                            {formatTime12h(m.effective_reminder_time)}
                            <span className="inherited">using {selectedUser.name}'s default</span>
                          </>
                        )}
                      </td>
                      <td>{m.default_dosage ? `${m.default_dosage} ${m.dosage_unit || ''}` : '—'}</td>
                      <td>{m.next_due_date || '—'}</td>
                      <td><button className="btn-link btn-danger-link" onClick={() => handleDeleteMed(m.id)}>Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="meds-section">
              <div className="section-header">
                <h3>{selectedUser.name}'s Calendar</h3>
              </div>
              <Calendar key={`admin-cal-${selectedUser.id}`} userId={selectedUser.id} onDayClick={handleDayClick} />

              {dayDetailLoading && <div className="calendar-loading">Loading…</div>}
              {dayDetail && (
                <div className="day-detail">
                  <h4>{dayDetail.date}</h4>
                  {dayDetail.daily.length === 0 && dayDetail.other.length === 0 ? (
                    <p className="empty-state">No medications scheduled or logged this day.</p>
                  ) : (
                    <>
                      {dayDetail.daily.map(d => (
                        <div key={d.medication_id} className={`day-detail-item ${d.taken ? 'taken' : 'missed'}`}>
                          <span className="day-detail-dot" />
                          <span className="day-detail-name">{d.name}</span>
                          <span className="day-detail-status">
                            {d.taken
                              ? `Taken at ${new Date(d.taken_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                              : `Missed — due ${formatTime12h(d.reminder_time)}`}
                          </span>
                        </div>
                      ))}
                      {dayDetail.other.map((o, i) => (
                        <div key={`other-${i}`} className="day-detail-item taken">
                          <span className="day-detail-dot" />
                          <span className="day-detail-name">{o.name}</span>
                          <span className="day-detail-status">
                            {o.type === 'interval' ? 'Interval dose' : 'As-needed dose'} at{' '}
                            {new Date(o.taken_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <p className="empty-state">Select a family member to view their details.</p>
        )}
      </div>
    </div>
    </div>
  );
}
